import { useCallback, useState } from 'react'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { performMutations, useMutation } from '@/lib/mutations'
import { pb, useStore } from '@/lib/pocketbase'
import type { RoleDialogState, UserRole } from '@/types/people'

export function useRoleDialog() {
    const { org: currentOrg } = useCurrentOrg({ throwIfNotFound: false })
    const [userOrgCollection] = useStore('user_org')
    const [roleDialog, setRoleDialog] = useState<RoleDialogState>({
        open: false,
        state: null,
    })
    const [selectedRole, setSelectedRole] = useState<UserRole>('clerical')

    const roleUpdateMutation = useMutation({
        mutationFn: async ({ memberId, role }: { memberId: string; role: UserRole }) => {
            if (!currentOrg) throw new Error('No current org')
            const userOrg = await pb
                .collection('user_org')
                .getFirstListItem(`user = "${memberId}" && org = "${currentOrg.id}"`)
            await performMutations(function* () {
                yield userOrgCollection.update(userOrg.id, draft => {
                    draft.role = role
                })
            })
        },
    })

    const openRoleDialog = useCallback((state: RoleDialogState['state'] & { role: UserRole }) => {
        setRoleDialog({
            open: true,
            state,
        })
        setSelectedRole(state.role)
    }, [])

    const closeRoleDialog = useCallback(() => {
        setRoleDialog({ open: false, state: null })
        setSelectedRole('clerical')
    }, [])

    const confirmRoleUpdate = useCallback(async () => {
        if (!roleDialog.state || !selectedRole) return

        await roleUpdateMutation.mutateAsync({
            memberId: roleDialog.state.memberId,
            role: selectedRole,
        })
        closeRoleDialog()
    }, [roleDialog.state, selectedRole, closeRoleDialog, roleUpdateMutation])

    return {
        roleDialog,
        selectedRole,
        openRoleDialog,
        closeRoleDialog,
        setSelectedRole,
        confirmRoleUpdate,
        isUpdating: roleUpdateMutation.isPending,
    }
}
